/**
 * Mock data for the redesigned Customer Feedback page (GROW-2236).
 *
 * UI-first: the backend does not yet expose most of these fields
 * (customer LTV, order context/items, email, contact status). This module
 * is shaped to match the expected future API response so that, once the
 * backend is ready, integration is a single swap of this data source.
 */

export const FEEDBACK_REVIEW_STATUS = {
  NEEDS_REPLY: 'needs_reply',
  EMAILED: 'emailed',
  CALLED: 'called',
  SKIPPED: 'skipped'
};

export const FEEDBACK_ORDER_TYPE = {
  DELIVERY: 'Delivery',
  PICKUP: 'Pickup'
};

export const FEEDBACK_SENTIMENT = {
  NEGATIVE: 'negative',
  POSITIVE: 'positive'
};

const firstName = (review) =>
  (review?.customerName || '').split(' ')[0] || 'there';

const firstItem = (review) => review?.order?.items?.[0] || 'your order';

/** Suggested-reply templates grouped by sentiment (V1, no promo). */
export const FEEDBACK_REPLY_TEMPLATES = {
  [FEEDBACK_SENTIMENT.NEGATIVE]: [
    {
      id: 'sorry',
      label: "We're sorry",
      text: (review) =>
        `Hi ${firstName(
          review
        )}, we're truly sorry your experience didn't meet expectations. Thank you for taking the time to share this with us — it really helps us understand where we fell short. We take feedback like yours seriously and would love the chance to make it right.`
    },
    {
      id: 'appreciate',
      label: 'We appreciate your feedback',
      text: (review) =>
        `Hi ${firstName(
          review
        )}, thank you for taking the time to share your feedback. We're sorry your experience wasn't what you hoped for. Your comments are really valuable to us and help us improve every day. We appreciate you letting us know.`
    },
    {
      id: 'improve',
      label: 'Help us improve',
      text: (review) =>
        `Hi ${firstName(
          review
        )}, we're sorry we fell short on your last order. Could you share a bit more about what went wrong so we can look into it on our end? We're committed to making things better and would love your input to get there.`
    },
    {
      id: 'another-chance',
      label: "We'd love another chance",
      text: (review) =>
        `Hi ${firstName(
          review
        )}, I'm really sorry about your recent experience. This is not the standard we hold ourselves to, and I take your feedback very seriously. We're looking into what went wrong and making sure it doesn't happen again. I'd love the opportunity to make it right`
    }
  ],
  [FEEDBACK_SENTIMENT.POSITIVE]: [
    {
      id: 'thank-you',
      label: 'Thank you!',
      text: (review) =>
        `Hi ${firstName(
          review
        )}, thank you so much for the kind words! We're thrilled you enjoyed the ${firstItem(
          review
        )} — it's one of our favorites too. We really appreciate you taking the time to share your experience, and we hope to see you again soon!`
    },
    {
      id: 'glad',
      label: "We're glad you enjoyed it",
      text: (review) =>
        `Hi ${firstName(
          review
        )}, we're so glad you enjoyed your order! It really means a lot to our team to hear that everything hit the mark. Thanks for taking the time to share your experience with us — it makes our day.`
    },
    {
      id: 'see-you',
      label: 'See you next time',
      text: (review) =>
        `Hi ${firstName(
          review
        )}, thanks so much for the great review! We loved having you and can't wait to serve you again. Next time you stop by, let us know — we'll make sure you get a warm welcome. See you soon!`
    }
  ]
};

/** Summary numbers for the 3 cards at the top of the page. */
export const FEEDBACK_SUMMARY = {
  averageRating: 4.5,
  reviewsCount: 126,
  needsReplyCount: 5,
  contactedPercent: 68,
  contactedCount: 86,
  contactedTotal: 126
};

const COMMENT_SHORT = 'Food was great, thanks!';
const COMMENT_MEDIUM =
  'The delivery was a little late but the food was still warm and tasted great. Will order again soon.';
const COMMENT_LONG =
  'Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry’s standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.';
const COMMENT_VERY_LONG =
  'Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry’s standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.';

export const FEEDBACK_REVIEWS = [
  {
    id: 'rev-1',
    customerName: 'John Smith',
    rating: 2,
    comment: COMMENT_VERY_LONG,
    createdAt: '2026-05-25T18:30:00Z',
    orderType: FEEDBACK_ORDER_TYPE.DELIVERY,
    status: FEEDBACK_REVIEW_STATUS.NEEDS_REPLY,
    unread: true,
    customer: {type: 'repeat', orderCount: 4, lifetimeValue: 185.0},
    order: {
      number: '102',
      total: 42.5,
      items: ['Margherita Pizza', 'Caesar Salad', 'Tiramisu']
    },
    contact: {email: 'john.smith@example.com', phone: '(555) 281-4837'}
  },
  {
    id: 'rev-2',
    customerName: 'Sarah Johnson',
    rating: 1,
    comment: COMMENT_SHORT,
    createdAt: '2026-05-25T16:10:00Z',
    orderType: FEEDBACK_ORDER_TYPE.PICKUP,
    status: FEEDBACK_REVIEW_STATUS.NEEDS_REPLY,
    unread: true,
    customer: {type: 'new', orderCount: 1, lifetimeValue: 23.75},
    order: {
      number: '101',
      total: 23.75,
      items: ['Chicken Burrito', 'Chips & Salsa']
    },
    contact: {email: 'sarah.johnson@example.com', phone: '(555) 909-1123'}
  },
  {
    id: 'rev-3',
    customerName: 'Rachel Torres',
    rating: 4,
    comment: COMMENT_MEDIUM,
    createdAt: '2026-05-25T14:02:00Z',
    orderType: FEEDBACK_ORDER_TYPE.DELIVERY,
    status: FEEDBACK_REVIEW_STATUS.NEEDS_REPLY,
    unread: false,
    customer: {type: 'new', orderCount: 1, lifetimeValue: 42.0},
    order: {
      number: '98',
      total: 42.0,
      items: ['Lamb Shawarma Plate', 'Hummus', 'Baklava']
    },
    contact: {email: 'rachel.torres@example.com', phone: '(555) 442-7781'}
  },
  {
    id: 'rev-4',
    customerName: 'Lisa Park',
    rating: 3,
    comment: COMMENT_LONG,
    createdAt: '2026-05-24T20:45:00Z',
    orderType: FEEDBACK_ORDER_TYPE.PICKUP,
    status: FEEDBACK_REVIEW_STATUS.NEEDS_REPLY,
    unread: true,
    customer: {type: 'repeat', orderCount: 2, lifetimeValue: 61.5},
    order: {number: '95', total: 28.0, items: ['Pad Thai', 'Spring Rolls']},
    contact: {email: 'lisa.park@example.com', phone: '(555) 663-2090'}
  },
  {
    id: 'rev-5',
    customerName: 'Tom Wilson',
    rating: 4,
    comment: COMMENT_VERY_LONG,
    createdAt: '2026-05-24T19:20:00Z',
    orderType: FEEDBACK_ORDER_TYPE.DELIVERY,
    status: FEEDBACK_REVIEW_STATUS.NEEDS_REPLY,
    unread: false,
    customer: {type: 'repeat', orderCount: 6, lifetimeValue: 240.25},
    order: {
      number: '92',
      total: 51.0,
      items: ['Double Cheeseburger', 'Fries', 'Milkshake']
    },
    contact: {email: 'tom.wilson@example.com', phone: '(555) 117-5544'}
  },
  {
    id: 'rev-6',
    customerName: 'Emily Chen',
    rating: 5,
    comment: COMMENT_SHORT,
    createdAt: '2026-05-24T12:15:00Z',
    orderType: FEEDBACK_ORDER_TYPE.DELIVERY,
    status: FEEDBACK_REVIEW_STATUS.EMAILED,
    unread: false,
    customer: {type: 'repeat', orderCount: 9, lifetimeValue: 412.0},
    order: {number: '88', total: 36.5, items: ['Sushi Combo', 'Miso Soup']},
    contact: {email: 'emily.chen@example.com', phone: '(555) 320-8841'}
  },
  {
    id: 'rev-7',
    customerName: 'Mike Davis',
    rating: 4,
    comment: COMMENT_MEDIUM,
    createdAt: '2026-05-23T21:05:00Z',
    orderType: FEEDBACK_ORDER_TYPE.PICKUP,
    status: FEEDBACK_REVIEW_STATUS.CALLED,
    unread: false,
    customer: {type: 'new', orderCount: 1, lifetimeValue: 19.0},
    order: {number: '84', total: 19.0, items: ['Falafel Wrap', 'Lemonade']},
    contact: {email: 'mike.davis@example.com', phone: '(555) 778-2231'}
  },
  {
    id: 'rev-8',
    customerName: 'Olivia Brown',
    rating: 2,
    comment: COMMENT_LONG,
    createdAt: '2026-05-23T13:40:00Z',
    orderType: FEEDBACK_ORDER_TYPE.DELIVERY,
    status: FEEDBACK_REVIEW_STATUS.SKIPPED,
    unread: false,
    customer: {type: 'repeat', orderCount: 3, lifetimeValue: 98.0},
    order: {number: '80', total: 33.25, items: ['Veggie Bowl', 'Iced Tea']},
    contact: {email: 'olivia.brown@example.com', phone: '(555) 556-9032'}
  }
];
